<script lang="ts">
  import { onMount } from 'svelte';
  import type { TenantSummary } from '@ucc/shared';
  import { ApiError, createTenant, fetchTenants, updateTenant } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let tenants = $state<TenantSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let formError = $state<string | null>(null);
  let saving = $state(false);

  let newName = $state('');
  let newLimit = $state(5);

  let editId = $state<string | null>(null);
  let editName = $state('');
  let editLimit = $state(5);

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');

  onMount(() => {
    void reload();
  });

  async function reload() {
    loading = true;
    error = null;
    try {
      const res = await fetchTenants();
      tenants = res.tenants;
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load tenants';
      tenants = [];
    } finally {
      loading = false;
    }
  }

  async function onCreate(e: Event) {
    e.preventDefault();
    formError = null;
    const name = newName.trim();
    if (!name) {
      formError = 'Tenant name is required.';
      return;
    }
    saving = true;
    try {
      await createTenant({ name, clientUserLimit: newLimit });
      newName = '';
      newLimit = 5;
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Create failed';
    } finally {
      saving = false;
    }
  }

  function startEdit(t: TenantSummary) {
    editId = t.id;
    editName = t.name;
    editLimit = t.clientUserLimit;
    formError = null;
  }

  function cancelEdit() {
    editId = null;
    editName = '';
    editLimit = 5;
  }

  async function saveEdit() {
    if (!editId) return;
    formError = null;
    const name = editName.trim();
    if (!name) {
      formError = 'Tenant name is required.';
      return;
    }
    saving = true;
    try {
      await updateTenant(editId, { name, clientUserLimit: editLimit });
      cancelEdit();
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Update failed';
    } finally {
      saving = false;
    }
  }
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Tenants</h2>
  <p class="sub">
    Client organizations. Each tenant caps how many client-user accounts it can have —
    clients see this limit in their own Settings but cannot raise it.
  </p>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can manage tenants.</p>
    </Card>
  {:else}
    <Card title="Create tenant" subtitle="Name must be unique · client-user limit default 5">
      <form class="form" onsubmit={(e) => void onCreate(e)}>
        <label class="field">
          <span class="label">Name</span>
          <input
            class="input"
            type="text"
            maxlength={120}
            placeholder="e.g. Acme Industrial"
            bind:value={newName}
            disabled={saving}
            required
          />
        </label>
        <label class="field">
          <span class="label">Client-user limit</span>
          <input class="input" type="number" min={1} bind:value={newLimit} disabled={saving} required />
        </label>
        {#if formError}
          <p class="err" role="alert">{formError}</p>
        {/if}
        <div class="actions">
          <Button type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create tenant'}</Button>
        </div>
      </form>
    </Card>

    <Card title="Tenants" subtitle={loading ? 'Loading…' : `${tenants.length} tenant(s)`}>
      {#if error}
        <p class="err" role="alert">{error}</p>
      {:else if loading}
        <p class="muted">Loading…</p>
      {:else if tenants.length === 0}
        <p class="muted">No tenants yet.</p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Client users</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each tenants as t (t.id)}
                {#if editId === t.id}
                  <tr class="editing">
                    <td colspan="4">
                      <div class="edit-row">
                        <input class="input" type="text" maxlength={120} bind:value={editName} disabled={saving} />
                        <input class="input" type="number" min={1} bind:value={editLimit} disabled={saving} />
                        <div class="edit-actions">
                          <button type="button" class="linkish" disabled={saving} onclick={() => void saveEdit()}
                            >Save</button
                          >
                          <button type="button" class="linkish muted" disabled={saving} onclick={cancelEdit}
                            >Cancel</button
                          >
                        </div>
                      </div>
                    </td>
                  </tr>
                {:else}
                  <tr>
                    <td>{t.name}</td>
                    <td class="mono">{t.slug}</td>
                    <td>{t.clientUserCount} / {t.clientUserLimit}</td>
                    <td class="row-actions">
                      <button type="button" class="linkish" disabled={saving} onclick={() => startEdit(t)}
                        >Edit</button
                      >
                    </td>
                  </tr>
                {/if}
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>
  {/if}
</div>

<style>
  .page {
    max-width: 56rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
  }
  .err {
    color: #f87171;
    margin: 0;
    font-size: 0.9rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.85rem;
  }
  .row-actions {
    white-space: nowrap;
    text-align: right;
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent, #3b82f6);
    cursor: pointer;
    font: inherit;
    padding: 0.15rem 0.35rem;
  }
  .linkish:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .linkish.muted {
    color: var(--text-muted);
  }
  .edit-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.35rem 0;
  }
  .edit-actions {
    display: flex;
    gap: 0.5rem;
  }
  .editing {
    background: color-mix(in srgb, var(--accent, #3b82f6) 8%, transparent);
  }
</style>
